import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, fmtDate } from "@/lib/api";
import { PageHead } from "@/components/admin/PageHead";

const CATEGORIES = [["", "All"], ["admin.", "Sign-ins"], ["deposit.", "Deposits"], ["withdrawal.", "Withdrawals"], ["client.", "Balances"],
  ["kyc.", "Verification"], ["stock.", "Stocks"], ["property.", "Properties"], ["enquiry.", "Enquiries"]];

const fmtDetails = (d) => Object.entries(d || {}).map(([k, v]) => (Array.isArray(v) ? `${k}: ${v[0]} → ${v[1]}` : `${k}: ${v}`)).join(" · ");

export default function AdminAudit() {
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/audit", { params: { action, q } }).then(({ data }) => setItems(data)).catch(() => {}), [action, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="admin-audit-page">
      <PageHead title="Audit log" subtitle="A permanent record of every admin action and sign-in. Entries cannot be edited or deleted.">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search summary or email" className="field pl-11" data-testid="admin-audit-search-input" />
        </div>
      </PageHead>
      <div className="mb-6 flex flex-wrap gap-2">
        {CATEGORIES.map(([k, l]) => (
          <button key={l} onClick={() => setAction(k)} data-testid={`admin-audit-filter-${l.toLowerCase()}`}
            className={`rounded-full border px-4 py-2.5 text-xs sm:py-1.5 transition-colors duration-200 ${action === k ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-line text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-audit-table">
          <thead><tr className="border-b border-line text-slate-500">
            <th className="px-6 py-4 font-medium">Time</th><th className="px-6 py-4 font-medium">Actor</th><th className="px-6 py-4 font-medium">Action</th>
            <th className="px-6 py-4 font-medium">Summary</th><th className="px-6 py-4 font-medium">IP</th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-line/60 align-top last:border-0" data-testid={`audit-row-${a.id}`}>
                <td className="px-6 py-4 whitespace-nowrap text-slate-400">{fmtDate(a.created_at)}</td>
                <td className="px-6 py-4 text-slate-300">{a.actor_email}</td>
                <td className="px-6 py-4 font-mono text-xs text-emerald-400">{a.action}</td>
                <td className="px-6 py-4 text-slate-200">{a.summary}{Object.keys(a.details || {}).length > 0 && <span className="mt-1 block text-xs text-slate-500">{fmtDetails(a.details)}</span>}</td>
                <td className="px-6 py-4 font-mono text-xs text-slate-500">{a.ip}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-500" data-testid="admin-audit-empty">No audit entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
